/**
 * URS-F395: Diálogo de diagnóstico de envíos de satisfacción para un estudio.
 * Permite a un admin ver, fila por fila, por qué cada cita del estudio en los
 * próximos N días genera o no un email automático de satisfacción. También
 * incluye un botón para forzar la ejecución inmediata del mailer.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Stethoscope, ArrowClockwise, CheckCircle, XCircle, Warning, PaperPlaneTilt, ClockCounterClockwise } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const decisionStyle = {
  would_send: { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: PaperPlaneTilt, label: 'Se enviará' },
  would_retry: { color: 'bg-amber-100 text-amber-800 border-amber-300', icon: ArrowClockwise, label: 'Reintento pendiente' },
  already_sent: { color: 'bg-sky-100 text-sky-800 border-sky-300', icon: CheckCircle, label: 'Ya enviado' },
  skip: { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle, label: 'Omitido' },
};

const reasonLabels = {
  no_invitation_yet: 'Sin invitación previa — se creará y enviará',
  pending_retry: 'Existe invitación sin envío, hay reintentos disponibles',
  exhausted_max_attempts: 'Agotados 4 intentos (bloqueado — ver botón Reiniciar)',
  invitation_already_sent: 'Invitación ya enviada previamente',
  no_active_satisfaction_assignments: 'La visita no tiene cuestionarios de satisfacción asignados',
  all_send_email_off: 'La visita tiene cuestionarios pero con envío automático desactivado',
  volunteer_no_email: 'El voluntario no tiene email registrado',
  missing_visit_id: 'Cita sin visit_id (dato inconsistente — ejecuta backfill)',
  missing_volunteer_id: 'Cita sin volunteer_id (dato inconsistente)',
  resolved_visit_by_code: 'visit_id ausente pero resuelto vía visit_code (recomendado ejecutar backfill)',
  // Legacy label (compat con versiones anteriores del backend)
  missing_visit_or_volunteer_id: 'Cita sin visit_id o volunteer_id (dato inconsistente)',
};

const reasonForStatus = (reason) => {
  if (!reason) return '';
  if (reason.startsWith('appt_status_not_eligible:')) {
    const st = reason.split(':')[1] || '?';
    return `Estado de cita "${st}" no elegible (sólo citada/confirmada/agendada/programada/reagendada)`;
  }
  if (reason.startsWith('missing_visit_id_and_no_match_for_code:')) {
    const code = reason.split(':')[1] || '?';
    return `Cita sin visit_id y ninguna visita del protocolo tiene code="${code}" (revisa el protocolo o corrige el visit_code de la cita)`;
  }
  return reasonLabels[reason] || reason;
};

export default function SatisfactionDiagnosticDialog({ studyId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [days, setDays] = useState(3);
  const [data, setData] = useState(null);

  const load = async (nextDays = days) => {
    if (!studyId) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/studies/${studyId}/satisfaction-diagnostic?days=${nextDays}`,
        { withCredentials: true }
      );
      setData(r.data);
    } catch (e) {
      toast.error(errText(e, 'No se pudo cargar el diagnóstico'));
    } finally {
      setLoading(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/satisfaction-mailer/run-now`,
        {},
        { withCredentials: true }
      );
      const s = r.data || {};
      if (s.skipped_reason === 'acumbamail_disabled') {
        toast.error('Acumbamail deshabilitado o sin api_key en Configuración > Integraciones');
      } else {
        toast.success(
          `Mailer ejecutado — enviados=${s.sent || 0}, reintentos=${s.retried || 0}, fallidos=${s.failed || 0}`
        );
      }
      // Recargar diagnóstico para reflejar cambios de estado
      await load();
    } catch (e) {
      toast.error(errText(e, 'Error ejecutando el mailer'));
    } finally {
      setRunning(false);
    }
  };

  // URS-F395b: backfill de appointments.visit_id ausentes para el estudio actual.
  const [backfilling, setBackfilling] = useState(false);
  const runBackfill = async () => {
    if (!studyId) return;
    if (!window.confirm('Se rellenará appointments.visit_id para las citas del estudio que sólo tengan visit_code. Idempotente. ¿Continuar?')) return;
    setBackfilling(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/studies/${studyId}/satisfaction-diagnostic/backfill-visit-ids`,
        {},
        { withCredentials: true }
      );
      const b = r.data || {};
      toast.success(`Backfill completado — actualizadas ${b.updated || 0} de ${b.scanned || 0} citas`);
      if (b.unmatched_codes && b.unmatched_codes.length > 0) {
        toast.warning(`Códigos sin coincidencia en el protocolo: ${b.unmatched_codes.join(', ')}`);
      }
      await load();
    } catch (e) {
      toast.error(errText(e, 'Error en backfill'));
    } finally {
      setBackfilling(false);
    }
  };

  const onOpen = (o) => {
    onOpenChange(o);
  };

  // Cargar diagnóstico automáticamente al abrir el diálogo.
  useEffect(() => {
    if (open && studyId && !data && !loading) {
      load();
    }
  }, [open, studyId, data, loading]);

  // URS-F423: historial de envios (todas las survey_invitations del estudio).
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all|failed|sent|pending|completed|expired|reopened|exhausted
  const [triggerFilter, setTriggerFilter] = useState('all'); // all|manual|auto
  const [emailQuery, setEmailQuery] = useState('');
  const [resendingId, setResendingId] = useState(null);

  const loadHistory = async () => {
    if (!studyId) return;
    setHistoryLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/studies/${studyId}/survey-invitations/history`,
        { withCredentials: true }
      );
      setHistory(r.data);
    } catch (e) {
      toast.error(errText(e, 'No se pudo cargar el historial'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const [activeTab, setActiveTab] = useState('diagnostic');
  useEffect(() => {
    if (open && activeTab === 'history' && !history && !historyLoading) {
      loadHistory();
    }
  }, [open, activeTab, history, historyLoading]);

  const resendInvitation = async (item) => {
    if (!window.confirm(`¿Reenviar el cuestionario "${item.questionnaire_name || ''}" al voluntario ${item.volunteer_name || item.study_volunteer_code} (${item.target_email})?`)) return;
    setResendingId(item.invitation_id);
    try {
      await axios.post(
        `${API_URL}/api/studies/${studyId}/survey-invitations/send`,
        {
          volunteer_id: item.volunteer_id,
          visit_id: item.visit_id,
          appointment_id: item.appointment_id || null,
          questionnaire_id: item.questionnaire_id,
        },
        { withCredentials: true }
      );
      toast.success('Cuestionario reenviado');
      await loadHistory();
    } catch (e) {
      toast.error(errText(e, 'Error reenviando el cuestionario'));
    } finally {
      setResendingId(null);
    }
  };

  const acumba = data?.acumbamail_config || {};
  const acumbaOk = data?.acumbamail_enabled;

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto" data-testid="satisfaction-diagnostic-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope size={20} className="text-[#1f3e80]" />
            Diagnóstico de envío de cuestionarios de satisfacción
          </DialogTitle>
        </DialogHeader>

        {/* Estado de Acumbamail */}
        {data && (
          <div className={`p-3 rounded-sm border ${acumbaOk ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-start gap-2">
              {acumbaOk ? <CheckCircle size={18} className="text-emerald-600 mt-0.5" /> : <Warning size={18} className="text-red-600 mt-0.5" />}
              <div className="text-xs">
                <p className="font-semibold">
                  Acumbamail: {acumbaOk ? 'habilitado' : 'DESHABILITADO — este es el motivo probable del bug'}
                </p>
                <ul className="mt-1 list-disc pl-4 space-y-0.5 text-stone-700">
                  <li>Toggle enabled: <b>{acumba.enabled ? 'sí' : 'no'}</b></li>
                  <li>api_key presente: <b>{acumba.api_key_present ? 'sí' : 'no'}</b></li>
                  <li>transactional_api_key presente: <b>{acumba.transactional_api_key_present ? 'sí' : 'no (los envíos fallarán aunque el toggle esté ON)'}</b></li>
                  <li>Remitente configurado: <b>{acumba.sender_email || 'no configurado'}</b></li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-stone-100">
            <TabsTrigger value="diagnostic" data-testid="sat-diag-tab-diagnostic">
              <Stethoscope size={14} className="mr-1 inline" /> Diagnóstico (próximas citas)
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="sat-diag-tab-history">
              <ClockCounterClockwise size={14} className="mr-1 inline" /> Historial de envíos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="diagnostic" className="space-y-3 pt-3">
        {/* Controles */}
        <div className="flex items-end gap-3 flex-wrap">
          <label className="text-xs text-stone-600">
            Ventana (días)
            <select
              data-testid="sat-diag-days"
              className="ml-2 border border-stone-300 rounded-sm text-sm px-2 py-1"
              value={days}
              onChange={e => { const d = parseInt(e.target.value, 10); setDays(d); load(d); }}
            >
              {[1, 2, 3, 5, 7, 14].map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <Button
            data-testid="sat-diag-reload"
            variant="outline"
            size="sm"
            onClick={() => load()}
            disabled={loading}
          >
            {loading ? 'Cargando…' : 'Recargar diagnóstico'}
          </Button>
          <Button
            data-testid="sat-diag-run-now"
            size="sm"
            onClick={runNow}
            disabled={running}
            className="bg-[#1f3e80] hover:bg-[#162f66] text-white"
          >
            <PaperPlaneTilt size={14} className="mr-1" />
            {running ? 'Ejecutando…' : 'Ejecutar mailer ahora'}
          </Button>
          <Button
            data-testid="sat-diag-backfill"
            variant="outline"
            size="sm"
            onClick={runBackfill}
            disabled={backfilling}
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
            title="Rellenar visit_id ausente en citas usando el visit_code"
          >
            {backfilling ? 'Rellenando…' : 'Backfill visit_id'}
          </Button>
          {data && (
            <span className="text-xs text-stone-500 ml-auto">
              Rango {data.range_start} → {data.range_end} · {data.total_appointments} citas
            </span>
          )}
        </div>

        {/* Tabla */}
        {data && data.rows && data.rows.length === 0 && (
          <div className="text-center py-8 text-stone-400 text-sm">
            No hay citas del estudio en la ventana seleccionada.
          </div>
        )}
        {data && data.rows && data.rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="sat-diag-table">
              <thead className="bg-stone-50 text-stone-600 uppercase tracking-wider">
                <tr>
                  <th className="text-left p-2">Cita</th>
                  <th className="text-left p-2">Visita</th>
                  <th className="text-left p-2">Voluntario</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">Estado cita</th>
                  <th className="text-left p-2">Asignación / Envío</th>
                  <th className="text-left p-2">Decisión</th>
                  <th className="text-left p-2">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, idx) => {
                  const decisionKey = r.decision || 'skip';
                  const style = decisionStyle[decisionKey] || decisionStyle.skip;
                  const Icon = style.icon;
                  return (
                    <tr key={r.appointment_id || idx} className="border-b border-stone-100 hover:bg-stone-50/60" data-testid={`sat-diag-row-${r.appointment_id}`}>
                      <td className="p-2 whitespace-nowrap">
                        <div className="font-semibold text-stone-800">{r.date}</div>
                        <div className="text-stone-500">{r.time}</div>
                      </td>
                      <td className="p-2 whitespace-nowrap">{r.visit_code || '—'}</td>
                      <td className="p-2 whitespace-nowrap">{r.volunteer_name || r.volunteer_id?.slice(0, 8)}</td>
                      <td className="p-2 whitespace-nowrap">
                        {r.volunteer_email ? (
                          <span className="font-mono text-[11px]">{r.volunteer_email}</span>
                        ) : (
                          <span className="text-red-600">sin email</span>
                        )}
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        <Badge variant="outline" className={`text-[10px] ${r.status_eligible ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-red-300 text-red-700 bg-red-50'}`}>
                          {r.appt_status || '—'}
                        </Badge>
                      </td>
                      <td className="p-2">
                        <div>Asignaciones activas: <b>{r.active_assignments}</b></div>
                        {r.send_email_off_count > 0 && (
                          <div className="text-red-700">Con envío OFF: {r.send_email_off_count}</div>
                        )}
                        {r.invitations && r.invitations.length > 0 && (
                          <ul className="mt-1 space-y-0.5">
                            {r.invitations.map((inv, i) => (
                              <li key={i} className="text-[11px] text-stone-600">
                                Q {inv.questionnaire_id?.slice(0, 8)}… ·
                                {' '}intentos: <b>{inv.send_attempts}</b>
                                {inv.sent_at && <> · <span className="text-emerald-700">enviado</span></>}
                                {inv.send_error && <div className="text-red-700">err: {inv.send_error}</div>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        <Badge variant="outline" className={`text-[10px] ${style.color}`}>
                          <Icon size={12} className="mr-1 inline" />
                          {style.label}
                        </Badge>
                      </td>
                      <td className="p-2 text-[11px] text-stone-700">
                        {reasonForStatus(r.reason)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
          </TabsContent>

          <TabsContent value="history" className="space-y-3 pt-3">
            {/* Controles filtros */}
            <div className="flex items-end gap-3 flex-wrap">
              <label className="text-xs text-stone-600">
                Estado
                <select
                  data-testid="sat-hist-status"
                  className="ml-2 border border-stone-300 rounded-sm text-sm px-2 py-1"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <option value="all">Todos</option>
                  <option value="failed">Fallidos</option>
                  <option value="exhausted">Agotados</option>
                  <option value="sent">Enviados</option>
                  <option value="pending">Pendientes</option>
                  <option value="completed">Completados</option>
                  <option value="expired">Expirados</option>
                  <option value="reopened">Reabiertos</option>
                </select>
              </label>
              <label className="text-xs text-stone-600">
                Origen
                <select
                  data-testid="sat-hist-trigger"
                  className="ml-2 border border-stone-300 rounded-sm text-sm px-2 py-1"
                  value={triggerFilter}
                  onChange={e => setTriggerFilter(e.target.value)}
                >
                  <option value="all">Todos</option>
                  <option value="manual">Manual</option>
                  <option value="auto">Automático</option>
                </select>
              </label>
              <label className="text-xs text-stone-600">
                Buscar
                <input
                  data-testid="sat-hist-search"
                  type="text"
                  placeholder="email, V-code, nombre..."
                  value={emailQuery}
                  onChange={e => setEmailQuery(e.target.value)}
                  className="ml-2 border border-stone-300 rounded-sm text-sm px-2 py-1 w-56"
                />
              </label>
              <Button
                data-testid="sat-hist-reload"
                variant="outline"
                size="sm"
                onClick={loadHistory}
                disabled={historyLoading}
              >
                {historyLoading ? 'Cargando…' : 'Recargar historial'}
              </Button>
              {history && (
                <div className="text-xs text-stone-500 ml-auto space-x-3">
                  <span>Total: <b>{history.total || 0}</b></span>
                  {history.counts && Object.entries(history.counts).filter(([, n]) => n > 0).map(([k, n]) => (
                    <span key={k}>{k}: <b>{n}</b></span>
                  ))}
                </div>
              )}
            </div>

            {history && history.items && (() => {
              const q = (emailQuery || '').toLowerCase().trim();
              const filtered = history.items.filter(it => {
                if (statusFilter !== 'all' && it.derived_status !== statusFilter) return false;
                if (triggerFilter !== 'all' && it.trigger !== triggerFilter) return false;
                if (q) {
                  const hay = `${it.target_email || ''} ${it.volunteer_name || ''} ${it.study_volunteer_code || ''}`.toLowerCase();
                  if (!hay.includes(q)) return false;
                }
                return true;
              });
              if (filtered.length === 0) {
                return (
                  <div className="text-center py-8 text-stone-400 text-sm">
                    No hay envíos que cumplan los filtros.
                  </div>
                );
              }
              const statusStyle = {
                sent: 'bg-emerald-100 text-emerald-800 border-emerald-300',
                completed: 'bg-sky-100 text-sky-800 border-sky-300',
                pending: 'bg-amber-100 text-amber-800 border-amber-300',
                failed: 'bg-red-100 text-red-800 border-red-300',
                exhausted: 'bg-red-200 text-red-900 border-red-400',
                expired: 'bg-stone-100 text-stone-600 border-stone-300',
                reopened: 'bg-violet-100 text-violet-800 border-violet-300',
              };
              const statusLabel = {
                sent: 'Enviado', completed: 'Completado', pending: 'Pendiente',
                failed: 'Fallido', exhausted: 'Agotado (4)', expired: 'Expirado',
                reopened: 'Reabierto',
              };
              return (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="sat-hist-table">
                    <thead className="bg-stone-50 text-stone-600 uppercase tracking-wider">
                      <tr>
                        <th className="text-left p-2">Fecha</th>
                        <th className="text-left p-2">Voluntario</th>
                        <th className="text-left p-2">Email</th>
                        <th className="text-left p-2">Visita / Cuest.</th>
                        <th className="text-left p-2">Origen</th>
                        <th className="text-left p-2">Estado envío</th>
                        <th className="text-left p-2">Entrega</th>
                        <th className="text-left p-2">Intentos</th>
                        <th className="text-left p-2">Error / Motivo</th>
                        <th className="text-left p-2">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(it => {
                        const delivery = it.delivery_status || '';
                        const deliveryStyle = {
                          delivered: 'bg-emerald-100 text-emerald-800 border-emerald-300',
                          bounced: 'bg-red-100 text-red-800 border-red-300',
                          spam: 'bg-orange-100 text-orange-800 border-orange-300',
                          opened: 'bg-sky-100 text-sky-800 border-sky-300',
                          clicked: 'bg-indigo-100 text-indigo-800 border-indigo-300',
                          unsubscribed: 'bg-stone-100 text-stone-700 border-stone-300',
                        };
                        const deliveryLabel = {
                          delivered: 'Entregado',
                          bounced: 'Rebotado',
                          spam: 'Spam',
                          opened: 'Abierto',
                          clicked: 'Click',
                          unsubscribed: 'Baja',
                        };
                        const deliveryTs = it.delivered_at || it.bounced_at || it.opened_at;
                        return (
                        <tr key={it.invitation_id} className="border-b border-stone-100 hover:bg-stone-50/60" data-testid={`sat-hist-row-${it.invitation_id}`}>
                          <td className="p-2 whitespace-nowrap">
                            <div className="font-semibold text-stone-800">{it.created_at ? new Date(it.created_at).toLocaleDateString('es-ES') : '—'}</div>
                            <div className="text-stone-500">{it.created_at ? new Date(it.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
                          </td>
                          <td className="p-2 whitespace-nowrap">
                            <div>{it.volunteer_name || '—'}</div>
                            <div className="text-stone-500">{it.study_volunteer_code || ''}</div>
                          </td>
                          <td className="p-2 whitespace-nowrap font-mono text-[10px]">
                            {it.target_email || <span className="text-red-600">sin email</span>}
                          </td>
                          <td className="p-2 whitespace-nowrap">
                            <div><b>{it.visit_code || '—'}</b></div>
                            <div className="text-stone-500">{it.questionnaire_name || ''}</div>
                          </td>
                          <td className="p-2 whitespace-nowrap">
                            <Badge variant="outline" className={`text-[10px] ${it.trigger === 'manual' ? 'bg-indigo-50 text-indigo-800 border-indigo-300' : 'bg-stone-50 text-stone-700 border-stone-300'}`}>
                              {it.trigger === 'manual' ? 'Manual' : 'Auto'}
                            </Badge>
                          </td>
                          <td className="p-2 whitespace-nowrap">
                            <Badge variant="outline" className={`text-[10px] ${statusStyle[it.derived_status] || 'bg-stone-100 text-stone-700 border-stone-300'}`}>
                              {statusLabel[it.derived_status] || it.derived_status}
                            </Badge>
                            {it.sent_at && (
                              <div className="text-[10px] text-stone-500 mt-0.5">{new Date(it.sent_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                            )}
                          </td>
                          <td className="p-2 whitespace-nowrap">
                            {delivery ? (
                              <>
                                <Badge variant="outline" className={`text-[10px] ${deliveryStyle[delivery] || 'bg-stone-100 text-stone-700 border-stone-300'}`}
                                  title={it.delivery_reason || ''}
                                  data-testid={`sat-hist-delivery-${it.invitation_id}`}>
                                  {deliveryLabel[delivery] || delivery}
                                </Badge>
                                {deliveryTs && (
                                  <div className="text-[10px] text-stone-500 mt-0.5">{new Date(deliveryTs).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                                )}
                              </>
                            ) : (
                              <span className="text-[10px] text-stone-400">Sin datos</span>
                            )}
                          </td>
                          <td className="p-2 text-center">{it.send_attempts}</td>
                          <td className="p-2 text-[11px] max-w-xs">
                            {it.send_error ? (
                              <span className="text-red-700" title={it.send_error}>{it.send_error.slice(0, 60)}{it.send_error.length > 60 ? '…' : ''}</span>
                            ) : it.delivery_reason ? (
                              <span className="text-orange-700" title={it.delivery_reason}>{it.delivery_reason.slice(0, 60)}{it.delivery_reason.length > 60 ? '…' : ''}</span>
                            ) : (
                              <span className="text-stone-400">—</span>
                            )}
                          </td>
                          <td className="p-2 whitespace-nowrap space-x-1">
                            {(it.derived_status === 'failed' || it.derived_status === 'exhausted' || it.derived_status === 'pending' || delivery === 'bounced') && (
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`sat-hist-resend-${it.invitation_id}`}
                                onClick={() => resendInvitation(it)}
                                disabled={resendingId === it.invitation_id}
                                className="text-[10px] h-7"
                              >
                                {resendingId === it.invitation_id ? 'Reenviando…' : 'Reenviar'}
                              </Button>
                            )}
                            {it.acumba_message_id && (
                              <a
                                href={`https://acumbamail.com/app/transaccional/messages/?search=${encodeURIComponent(it.acumba_message_id)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] text-[#1f3e80] hover:underline inline-flex items-center px-2 h-7 border border-stone-300 rounded-sm"
                                title="Abrir este mensaje en el panel de Acumbamail"
                                data-testid={`sat-hist-acumba-link-${it.invitation_id}`}
                              >
                                Acumbamail
                              </a>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
            {!history && historyLoading && (
              <div className="text-center py-8 text-stone-400 text-sm">Cargando historial…</div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sat-diag-close">
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
